import { useMemo } from "react";
import { bool, func } from "prop-types";
import { useTranslation } from "react-i18next";
import { useXlsImporterSettings } from "@tools";
import { Button, ButtonSwitch, Checkbox } from "@components";
import { Grid, Switch } from "@material-ui/core";
import { ArrowBack } from "@assets";

export const ImporterActions = ({
  cancelImport,
  finishImporting,
}) => {
  const { t } = useTranslation();
  const settings = useXlsImporterSettings();

  const { steps, activeStep, useMinMax, applyReverseBuffers, importTimeModifier, hasDefaultStructure } = settings;

  const stepsCompleted = useMemo(
    () =>
      steps.reduce((p, c) => {
        return p && c.completed;
      }, true),
    [steps]
  );

  const nextStepDisabled = useMemo(
    () => steps.findIndex((s, i) => i === activeStep + 1 && s.locked) >= 0,
    [steps, activeStep]
  );

  const nextStep = () => {
    const nextIndex = steps.findIndex((s, i) => i > activeStep && !s.locked);
    if (nextIndex >= 0) settings.handleActiveStepChange(nextIndex);
  };

  const prevStep = () => {
    settings.handleActiveStepChange(activeStep - 1);
  };

  return (
    <>
      <Grid item container alignItems="center" className="w-max" spacing={2}>
        {
          activeStep === 3 &&
          <>
            <Grid
              item container
              wrap="nowrap"
              alignItems="center"
              className="w-max"
            >
              <p className="mr-2">{t("views.import.time")}</p>
              <ButtonSwitch
                value={importTimeModifier}
                setValue={(v) => settings.setImportTimeModifier(v)}
                values={[
                  { value: 1, label: t("times.hour") },
                  { value: 0, label: t("times.day") },
                ]}
              />
            </Grid>
            <Grid
              item container
              wrap="nowrap"
              alignItems="center"
              className="w-max"
            >
              <p className="mr-2">{t("views.editor.min_max")}</p>
              <Switch
                color="primary"
                checked={useMinMax}
                onChange={() => settings.setMinMax(!useMinMax)}
                name={
                  useMinMax ? "Revert to fixed values" : "Allow min-max values"
                }
              />
            </Grid>
            {
              hasDefaultStructure &&
              <Grid
                item container
                wrap="nowrap"
                alignItems="center"
                className="w-max"
              >
                <Checkbox
                  checked={applyReverseBuffers}
                  name={applyReverseBuffers ? "Estimate uses buffers" : "Estimate does not use buffers"}
                  onChange={() => settings.setApplyReverseBuffers(!applyReverseBuffers)}
                  label={t("views.editor.uses_buffers")}
                />
              </Grid>
            }
          </>
        }
      </Grid>
      <Grid item container alignItems="center" className="w-max" spacing={1}>
        <Button
          onClick={cancelImport}
          variant="outlined"
          className="m-1"
        >
          {t("views.import.cancel")}
        </Button>
        {
          activeStep > 0 &&
          <Button
            variant="outlined"
            icon={<ArrowBack />}
            onClick={prevStep}
            disabled={activeStep === 0}
            className="m-1"
          >
            {t("views.import.prev_step")}
          </Button>
        }
        {
          activeStep + 1 < steps.length
            ? <Button
            variant="contained"
            endIcon={<ArrowBack className="rotate-180" />}
            onClick={nextStep}
            disabled={nextStepDisabled}
            className="m-1"
          >
            {t("views.import.next_step")}
          </Button>
            : <Button
              color="primary"
              variant="contained"
              onClick={finishImporting}
              disabled={!stepsCompleted}
              className="m-1"
            >
              {t("views.import.confirm")}
            </Button>
        }
      </Grid>
    </>
  );
}

ImporterActions.propTypes = {
  cancelImport: func,
  finishImporting: func,
  useBufferHoursAdjustOption: bool,
  buffersUsed: bool,
};
